import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { AiOutlineMenu } from "react-icons/ai";

import logo from "../../assets/images/logo.png";
import noItems from "../../assets/images/noItems.jpg";
import { resetCar } from "../../redux/car";
import { createdFromOrder, createdFromVoiceOrder } from "../../redux/order";
import { isValidUrl } from "../../helpers/isValidUrl";
import { getSkuCategories } from "../../services/sku";
import { getFollowUpOrderDetails } from "../../services/order";
import { getAllMechanicCollections } from "../../services/mechanic";
import Header from "../../components/Categories/Header/Header";
import Ripple from "../../components/ui/spinners/ripple/Ripple";
import CarDropdowns from "../../components/CarDropdowns/CarDropdowns";
import ProductsFilter from "../../components/ProductListing/ProductsFilter/ProductsFilter";
import OverLay from "../../components/ui/OverLay/OverLay";
import MechanicAutoComplete from "../../components/MechanicAutoComplete/MechanicAutoComplete";
import Banner from "../../components/Banner/Banner";
import PromoBanner from "../../components/PromoBanner/PromoBanner";

const Categories = ({ brands, brandsLoading }) => {
  // redux
  const allCarBrands = useSelector((state) => state.car.allCarBrands);
  const mechanic = useSelector((state) => state.mechanic);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { selectedCar } = useSelector((state) => state.car);
  const [carListLoading, setCarListLoading] = useState(false);
  const [carList, setCarList] = useState([]);
  const [filterParams, setFilterParams] = useState({});

  const [filtersShown, setFiltersShown] = useState(false);

  const showProductsFilters = () => {
    setFiltersShown(true);
  };

  const hideProductsFilter = () => {
    setFiltersShown(false);
  };

  const dispatch = useDispatch();

  useEffect(() => {
    if (selectedCar.brand) {
      setFilterParams((prevParams) => {
        const updatedParams = { ...prevParams };
        updatedParams.carBrandId = selectedCar.brand.id;
        delete updatedParams["carModelId"];
        delete updatedParams["carStartYear"];
        delete updatedParams["carEndYear"];
        delete updatedParams["carId"];
        return updatedParams;
      });
    }
    if (selectedCar.model) {
      setFilterParams((prevParams) => {
        const updatedParams = { ...prevParams };
        updatedParams.carModelId = selectedCar.model.id;
        delete updatedParams["carStartYear"];
        delete updatedParams["carEndYear"];
        delete updatedParams["carId"];
        return updatedParams;
      });
    }
    if (selectedCar.year) {
      setFilterParams((prevParams) => {
        const updatedParams = { ...prevParams };
        updatedParams.carStartYear = selectedCar.year.startYear;
        updatedParams.carEndYear = selectedCar.year.endYear;
        delete updatedParams["carId"];
        return updatedParams;
      });
    }
    if (selectedCar.carDetails) {
      setFilterParams((prevParams) => ({
        ...prevParams,
        carId: selectedCar.carDetails.id,
      }));
    }
  }, [selectedCar]);

  const filterSkus = useCallback((param) => {
    setFilterParams((prevParams) => ({ ...prevParams, ...param }));
  }, []);

  const resetFiltersHandler = () => {
    setFilterParams({});
    dispatch(resetCar());
  };
  useEffect(() => {
    if (allCarBrands.length === 0) {
      setCarListLoading(true);
    } else {
      setCarList(allCarBrands);
      setCarListLoading(false);
    }
  }, [allCarBrands]);

  // fetch categories
  useEffect(() => {
    const getCategories = async () => {
      const categoriesFilters = {
        carBrandId: selectedCar?.brand?.id,
        carModelId: selectedCar?.model?.id,
        startYear: selectedCar?.year?.startYear,
        endYear: selectedCar?.year?.endYear,
        carId: selectedCar?.carDetails?.id,
      };
      setLoading(true);
      try {
        const response = await getSkuCategories(categoriesFilters);
        let { data: categoriesList } = response.data;

        let mechanicCollections = [];

        if (mechanic?.id) {
          const mechanicCollectionsResponse = await getAllMechanicCollections(
            mechanic?.id
          );

          const {
            data: { data },
          } = mechanicCollectionsResponse;

          mechanicCollections = data?.map((category) => ({
            categoryName: category.name,
            categoryImage: category.imageUrl,
            webhookUrl: category.webhookUrl,
          }));
        }

        categoriesList = [...mechanicCollections, ...categoriesList];

        setCategories(categoriesList);
      } catch (error) {
        console.log(error);
      }
      setLoading(false);
    };

    getCategories();
  }, [selectedCar, mechanic?.id]);

  // query params
  const [searchParams] = useSearchParams();

  const voiceOrderId = Number(searchParams.get("voiceOrderId"));

  const createdFromOrderId = Number(searchParams.get("createdFromOrderId"));

  const [mechanicId, setMechanicId] = useState(
    Number(searchParams.get("mechanicId"))
  );

  const [branchId, setBranchId] = useState(
    Number(searchParams.get("branchId"))
  );

  const [car, setCar] = useState();

  const fetchFollowUpOrderDetails = useCallback(async () => {
    try {
      const response = await getFollowUpOrderDetails(createdFromOrderId);

      const {
        data: {
          orderDetails: {
            mechanic: { id: mechanicId, branchId },
            car,
            salesRep: { id: salesRepId },
          },
        },
      } = response;

      setMechanicId(mechanicId);
      setBranchId(branchId);
      setCar(car);
      dispatch(
        createdFromOrder({
          salesRepId,
          createdFromOrderId,
          isHighPriority: true,
        })
      );
    } catch (err) {
      console.error(err);
    }
  }, [createdFromOrderId, dispatch]);

  useEffect(() => {
    if (createdFromOrderId) {
      fetchFollowUpOrderDetails();
    }
  }, [createdFromOrderId, fetchFollowUpOrderDetails]);

  useEffect(() => {
    if (voiceOrderId) {
      dispatch(createdFromVoiceOrder({ voiceOrderId }));
    }
  }, [voiceOrderId, dispatch]);

  // categories grid
  const categoriesGrid =
    categories &&
    categories.map((category, index) => (
      <Link
        to={
          category.id
            ? `/skus?categoryName=${category.categoryName}&categoryId=${category.id}`
            : `/sku/mechanicCollection?categoryName=${category.categoryName}&webhookUrl=${category.webhookUrl}`
        }
        key={index}
        className="min-w-0 min-h-fit cursor-pointer border border-light-gray rounded-lg py-4 flex flex-col item-center gap-8 hover:scale-105 transition-all"
      >
        <div className="w-40 h-40 mx-auto">
          <img
            src={
              isValidUrl(category.categoryImage) ? category.categoryImage : logo
            }
            alt={category.categoryName}
            className="mx-auto"
          />
        </div>
        <p className="text-lg text-center font-semibold">
          {category.categoryName}
        </p>
      </Link>
    ));

  let categoriesContent;
  if (loading) {
    categoriesContent = <Ripple className="border-blue-900" />;
  } else if (categories.length !== 0) {
    categoriesContent = (
      <section className="self-start grow grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {categoriesGrid}
      </section>
    );
  } else {
    categoriesContent = (
      <div className="mx-auto w-fit text-center">
        <img src={noItems} alt="no items match your filters" />
        <h2>لا توجد عناصر تطابق عوامل التصفية الخاصة بك</h2>
      </div>
    );
  }
  return (
    <>
      <div className="flex flex-col min-h-screen">
        <Header filterSkus={filterSkus} />
        <main dir="rtl" className="mt-4 mb-60">
          <CarDropdowns car={car} />
          <MechanicAutoComplete mechanicId={mechanicId} branchId={branchId} />
          <Banner />
          <PromoBanner />
          <h2 className={classNames("text-center mb-2")}>
            أختر واحد من الاقسام الرئيسية لتصفح المنتجات المتاحه
          </h2>

          <div className="mt-10 mb-32 pl-4 pr-1 sm:pl-16 sm:pr-16 flex gap-4 sm:gap-8 max-w-full">
            <AiOutlineMenu
              size={25}
              className="cursor-pointer block lg:hidden shrink-0"
              onClick={showProductsFilters}
            />
            {filtersShown && (
              <OverLay clicked={hideProductsFilter} breakPoint={1024} />
            )}
            <ProductsFilter
              carList={carList}
              carListLoading={carListLoading}
              brands={brands}
              brandsLoading={brandsLoading}
              filterParams={filterParams}
              filterSkus={filterSkus}
              filtersShown={filtersShown}
              reset={resetFiltersHandler}
            />
            {categoriesContent}
          </div>
        </main>
      </div>
    </>
  );
};

export default Categories;
